/* eslint-disable */
import React from 'react';
import Highcharts from 'highcharts/highcharts.js';
import HighchartsReact from 'highcharts-react-official';
import CELogo from '../../../assets/images/CElogo.jpg';
import CEsignature from '../../../assets/images/CEsignature.png';
import CEHorizontalLogo from '../../../assets/images/CEHorizontalLogo.png';
import celeaf from '../../../assets/images/celeaf.png';
import CELogoCircle from '../../../assets/images/CELogoCircle.png';
import { formatAccountingNumber, parseMoney, percentRounded, usStates } from '../utils/index';
import { premiumChart, netPremiumWrittenToSurplus, netLeverageRatio, reinsuranceToGPW, liquidityRatio, combinedRatio, opexRatio, breakEvenRatio, surplusToGRIF000s } from './CeChartHelper';

const MinnesotaReport = (props) => {
    const abbreviation = props.abbreviation;
    const fye = props.filters.year
    const selectedYear = props.filters.year
    const calcs = props.calcs.you;
    const peerCalcs = props.calcs.avg;
    const calcsYOY = calcs?.[selectedYear]?.['change_over_year']
    const peersYOY = props.calcs.avg?.[selectedYear]?.['change_over_year']
    const formattedDate = props.dateIssued;
    const years = props?.filters?.year ? [props?.filters?.year - 4, props?.filters?.year - 3, props?.filters?.year - 2, props?.filters?.year - 1, props?.filters?.year] : []
    const axisFormat = (format, num) => {
        switch (format) {
            case 'percent':
                return `${Highcharts.numberFormat((num * 100), 0, '', ',')}%`;
            case 'decimal':
                return Highcharts.numberFormat(num, 0);
            case 'ratio':
                return `${Highcharts.numberFormat(num, 0, '.', ',')}:1`;
            case 'money':
                if (Math.abs(num) >= 1000000) {
                    return num >= 0 ? `$${Highcharts.numberFormat(num / 1000000, 1)}M` : `-$${Highcharts.numberFormat(Math.abs(num / 1000000), 1)}M`;
                } else {
                    return num >= 0 ? `$${Highcharts.numberFormat(num / 1000, 0)}K` : `-$${Highcharts.numberFormat(Math.abs(num / 1000), 0)}K`;
                }
            case 'wholeNumber':
                return Math.round(num)
        }
    }

    const getPeersSnapshot = (calcsActualPeers) => {
        let tableRows = []
        let peers = calcsActualPeers ? calcsActualPeers.sort((a, b) => b['gross_premium_written'] - a['gross_premium_written']) : [];

        if (peers && peers.length > 0) {
            peers.forEach(r => {
                if (r.gross_premium_written && r.surplus) {
                    const rowStyle = r.client_name == props.client_name ? { 'fontWeight': 'bold' } : {};
                    let companyRealName = r.display_name;
                    let splitName = companyRealName.split(' ');
                    let stateEnd = splitName[splitName.length - 1]
                    let stateAbbr = usStates.find(s => s[0] == stateEnd);
                    if (stateAbbr) {
                        splitName[splitName.length - 1] = stateAbbr[1];
                    }
                    companyRealName = splitName.join(' ')

                    tableRows.push(
                        <tr style={rowStyle}>
                            <td>{companyRealName}</td>
                            <td className='ce-financial-sum-table-cell'>{axisFormat('money', r.gross_premium_written)}</td>
                            <td className='ce-financial-sum-table-cell'>{axisFormat('money', r.surplus)}</td>
                        </tr>
                    )
                }
            })
    
            return (
                <table className='ce-peers-table'>
                    <thead>
                        <tr>
                            <td></td>
                            <td style={{ 'width': '75px', 'textAlign': 'center', 'verticalAlign': 'bottom', 'fontSize': '14px', 'fontWeight': 'bold' }}>Premium Written</td>
                            <td style={{ 'width': '75px', 'textAlign': 'center', 'verticalAlign': 'bottom', 'fontSize': '14px', 'fontWeight': 'bold' }}>Surplus</td>
                        </tr>
                    </thead>
                    <tbody>
                        {tableRows}
                    </tbody>
                </table>
            )   
        } else {
            return null
        }

    }

    const getSummaryTable = (calcs) => {
        let tableRows = []
        const rows = [
            {
                key: 'empty_row'
            },
            {
                key: "cash_and_invested_assets",
                metric: "Cash & Invested Assets",
                dollarSign: true
            },
            {
                key: "admitted_assets",
                metric: "Admitted Assets",
            },
            {
                key: "surplus",
                metric: "Surplus",
            },
            {
                key: 'empty_row'
            },
            {
                key: "gross_premium_written",
                metric: "Premium Written",
            },
            {
                key: "net_premium_written",
                metric: "Net Premium Written",
            },
            {
                key: "premium_earned",
                metric: "Premium Earned",
            },
            {
                key: "underwriting_gain_loss",
                metric: "Underwriting Gain (Loss)",
            },
            {
                key: "net_income_before_income_taxes",
                metric: "Net Profit Before Taxes",
            },
        ]

        tableRows = rows.map(r => {
            if (r.key == 'empty_row') {
                return (
                    <tr style={{ 'height': '10pt' }}>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                    </tr>
                )
            } else {
                let yearVals = years.map(y => {
                    return formatAccountingNumber(calcs.you?.[y]?.[r.key], { showDollarSign: r.dollarSign, parenthesesForNegatives: true, round: true, decimalPlaces: 0 })
                })
                return (
                    <tr>
                        <td>{r.metric}</td>
                        <td className='ce-financial-sum-table-cell'>{yearVals[0]}</td>
                        <td className='ce-financial-sum-table-cell'>{yearVals[1]}</td>
                        <td className='ce-financial-sum-table-cell'>{yearVals[2]}</td>
                        <td className='ce-financial-sum-table-cell'>{yearVals[3]}</td>
                        <td className='ce-financial-sum-table-cell'>{yearVals[4]}</td>
                    </tr>
                )
            }
        })

        return (
            <table className='ce-financial-sum-table'>
                <thead>
                    <tr>
                        <td></td>
                        {years.map(y => <td style={{ 'textDecoration': 'underline', 'textAlign': 'center', 'fontWeight': 'bold' }}>{y}</td>)}
                    </tr>
                </thead>
                <tbody>
                    {tableRows}
                </tbody>
            </table>
        )
    }

    const premiumWritten = percentRounded(calcs?.[selectedYear]?.premium_written_five_yr_gr, 1, true)
    const netPremiumWritten = percentRounded(calcs?.[selectedYear]?.net_premium_written_five_yr_gr, 1, true)
    const premiumEarned = percentRounded(calcs?.[selectedYear]?.premium_earned_five_yr_gr, 1, true)
    const comparePremium = calcs?.[selectedYear]?.premium_written_five_yr_gr > 0 && calcs?.[selectedYear]?.net_premium_written_five_yr_gr > 0 && calcs?.[selectedYear]?.premium_earned_five_yr_gr > 0 ? 'grew' : 'changed';

    const net_premium_to_surplus_ratio = percentRounded(calcs?.[selectedYear]?.net_premium_to_surplus_ratio, 100, true)
    const peers_net_premium_to_surplus_ratio = percentRounded(peerCalcs?.[selectedYear]?.net_premium_to_surplus_ratio, 100, true)
    const min_peers_net_premium_to_surplus_ratio = percentRounded(props.calcs?.min?.net_premium_to_surplus_ratio, 100, true)
    const max_peers_net_premium_to_surplus_ratio = percentRounded(props.calcs?.max?.net_premium_to_surplus_ratio, 100, true)
    const compare_net_premium_to_surplus_ratio = calcs?.[selectedYear]?.net_premium_to_surplus_ratio > peerCalcs?.[selectedYear]?.net_premium_to_surplus_ratio ? 'higher' : 'lower';
    const compare_capacity = compare_net_premium_to_surplus_ratio == 'higher' ? 'lower' : 'greater';

    const net_leverage_ratio = percentRounded(calcs?.[selectedYear]?.net_leverage_ratio, 100, true)
    const peers_net_leverage_ratio = percentRounded(peerCalcs?.[selectedYear]?.net_leverage_ratio, 100, true)
    const min_peers_net_leverage_ratio = percentRounded(props.calcs?.min?.net_leverage_ratio, 100, true)
    const max_peers_net_leverage_ratio = percentRounded(props.calcs?.max?.net_leverage_ratio, 100, true)

    const reinsurance_to_gpw_ratio = percentRounded(calcs?.[selectedYear]?.reinsurance_to_gpw_ratio, 100, true)
    const peers_reinsurance_to_gpw_ratio = percentRounded(peerCalcs?.[selectedYear]?.reinsurance_to_gpw_ratio, 100, true)
    const min_peers_reinsurance_to_gpw_ratio = percentRounded(props.calcs?.min?.reinsurance_to_gpw_ratio, 100, true)
    const max_peers_reinsurance_to_gpw_ratio = percentRounded(props.calcs?.max?.reinsurance_to_gpw_ratio, 100, true)

    const liquidity_ratio = percentRounded(calcs?.[selectedYear]?.liquidity_ratio, 100, true);
    const peers_liquidity_ratio = percentRounded(peerCalcs?.[selectedYear]?.liquidity_ratio, 100, true)
    const min_peers_liquidity_ratio = percentRounded(props.calcs?.min?.liquidity_ratio, 100, true)
    const max_peers_liquidity_ratio = percentRounded(props.calcs?.max?.liquidity_ratio, 100, true)

    const compareUnderwritingGainLoss = calcs?.[selectedYear]?.underwriting_gain_loss > 0 ? 'gain' : 'loss';
    const underwriting_gain_loss = formatAccountingNumber(calcs?.[selectedYear]?.underwriting_gain_loss, { showDollarSign: true, parenthesesForNegatives: true, round: true, decimalPlaces: 0 });

    const compareNetLossAndOpex = (calcsYOY?.premium_earned > 0 && calcsYOY?.net_losses_incurred > 0 && calcsYOY?.operating_expenses > 0) ? 'increased' : (calcsYOY?.premium_earned < 0 && calcsYOY?.net_losses_incurred < 0 && calcsYOY?.operating_expenses < 0) ? 'decreased' : 'changed';
    const net_premium_writtenYOY = percentRounded(calcsYOY?.premium_earned, 1, true);
    const net_losses_incurredYOY = percentRounded(calcsYOY?.net_losses_incurred, 1, true);
    const operating_expensesYOY = percentRounded(calcsYOY?.operating_expenses, 1, true);

    const peers_net_premium_writtenYOY = percentRounded(peersYOY?.premium_earned, 1, true);
    const peers_net_losses_incurredYOY = percentRounded(peersYOY?.net_losses_incurred, 1, true);
    const peers_operating_expensesYOY = percentRounded(peersYOY?.operating_expenses, 1, true);

    const combined_ratio_no_one_year = percentRounded(calcs?.[selectedYear]?.combined_ratio_no_one_year, 100, true)
    const peers_combined_ratio_no_one_year = percentRounded(peerCalcs?.[selectedYear]?.combined_ratio_no_one_year, 100, true)
    const min_peers_combined_ratio_no_one_year = percentRounded(props.calcs?.min?.combined_ratio_no_one_year, 100, true)
    const max_peers_combined_ratio_no_one_year = percentRounded(props.calcs?.max?.combined_ratio_no_one_year, 100, true)

    const combined_ratio_no_five_year_avg = percentRounded(calcs?.[selectedYear]?.combined_ratio_no_five_year_avg, 100, true)
    const peers_combined_ratio_no_five_year_avg = percentRounded(peerCalcs?.[selectedYear]?.combined_ratio_no_five_year_avg, 100, true)
    const min_peers_combined_ratio_no_five_year_avg = percentRounded(props.calcs?.min?.combined_ratio_no_five_year_avg, 100, true)
    const max_peers_combined_ratio_no_five_year_avg = percentRounded(props.calcs?.max?.combined_ratio_no_five_year_avg, 100, true)

    const operating_expense_ratio = percentRounded(calcs?.[selectedYear]?.operating_expense_ratio, 100, true)
    const peers_operating_expense_ratio = percentRounded(peerCalcs?.[selectedYear]?.operating_expense_ratio, 100, true)
    const min_peers_operating_expense_ratio = percentRounded(props.calcs?.min?.operating_expense_ratio, 100, true)
    const max_peers_operating_expense_ratio = percentRounded(props.calcs?.max?.operating_expense_ratio, 100, true)

    const break_even_ratio = percentRounded(calcs?.[selectedYear]?.break_even_ratio_no, 100, true)
    const peers_break_even_ratio = percentRounded(peerCalcs?.[selectedYear]?.break_even_ratio_no, 100, true)
    const min_peers_break_even_ratio = percentRounded(props.calcs?.min?.break_even_ratio_no, 100, true)
    const max_peers_break_even_ratio = percentRounded(props.calcs?.max?.break_even_ratio_no, 100, true)
    const net_loss_ratio = percentRounded(calcs?.[selectedYear]?.net_loss_ratio, 100, true)

    const surplus = calcs?.[selectedYear]?.surplus ? formatAccountingNumber(calcs?.[selectedYear]?.surplus, { showDollarSign: true, parenthesesForNegatives: true, round: true, decimalPlaces: 0 }) : '$0';
    const difSurplus = Math.abs(calcs?.[selectedYear]?.surplus - calcs?.[selectedYear - 1]?.surplus);
    const finalDifSurplus = formatAccountingNumber(difSurplus, { showDollarSign: true, parenthesesForNegatives: true, round: true, decimalPlaces: 0 });
    const compare_surplus =  calcs?.[selectedYear]?.surplus > calcs?.[selectedYear - 1]?.surplus ? 'increased' : 'decreased';
    const surplusYOY = percentRounded(calcsYOY?.surplus)

    return (
        <div className='ce-report-container'>
            <div className='ce-cover-page'>
                <div className='ce-cover-page-info'>
                    <div>{props.real_name?.toUpperCase()}</div>
                    <div>BENCHMARKING REPORT</div>
                    <div>{`DECEMBER 31, ${props?.filters?.year}`}</div>
                </div>
                <div className='ce-cover-page-footer'>
                    <img className='ce-logo-jpg' src={CELogo} alt="CE Logo" />
                </div>
            </div>
            <div className='ce-portrait-view-w-letterhead'>
                {props.letterHead
                    ? (<div className='ce-letterhead-intro-top-container'>
                            <div className='ce-letterhead-intro-top'>
                                <div className='ce-web-address'>cainellsworth.com</div>
                            </div>
                            <div className='ce-white-circle-logo'></div>
                            <img className='ce-horizontal-logo' src={CEHorizontalLogo} alt="CE Logo Horizontal" />
                        </div>)
                    : <div className='ce-letterhead-intro-top-container' />
                }
                <div className='ce-page-container'>
                    <div className='ce-intro-letter'>
                        <div className='ce-intro-company-header'>
                            <div>{props.real_name}</div>
                            <div>{props.location}</div>
                        </div>
                        <div className='ce-intro-dear'>To the Management and Directors:</div>
                        <div className='ce-paragraph'>
                            The purpose of this report is to provide management and the board of directors of {props.real_name} (hereinafter referred to as {abbreviation}) with financial information and analysis that will assist
                            them in benchmarking their performance against peers in the P&C insurance industry.<span className='sentence-end'></span>The information
                            contained herein was derived from annual financial regulatory filings that were furnished to us by you.
                        </div>
                        <div className='ce-paragraph'>
                            The annual regulatory filing for {abbreviation} and the report thereon, dated {formattedDate}, should be read
                            in conjunction with this report.<span className='sentence-end'></span>The information and comments contained herein are presented for
                            supplementary analysis purposes only.<span className='sentence-end'></span>The following companies have agreed to provide their annual financial filings, 
                            which comprise the peer data in this report.<span className='sentence-end'></span>Except for certain entities, we have not audited, reviewed, or compiled the filings from which the peer data was obtained.
                        </div>
                    </div>
                    <div className='ce-financial-peers-snapshot'>
                        {getPeersSnapshot(props.calcs?.actual_peers)}
                    </div>
                    <div className='ce-financial-summary'>
                        <div className='ce-section-title' style={{ 'marginBottom': '0' }}>Financial Summary</div>
                        {getSummaryTable(props.calcs)}
                    </div>
                </div>
                {props.letterHead
                    ? (<div className='ce-letterhead-intro-bottom'>
                            <div className='ce-letterhead-bottom-slogan'>
                                <div className='ce-slogan'>
                                    BEYOND THE NUMBERS
                                </div>
                            </div>
                            <div className='ce-contact-container'>
                                <div className='ce-contact-info-container'>
                                    <img className='ce-leaf-icon' src={celeaf} alt="CE Leaf icon" /> 
                                    <div className='ce-contact-info'>
                                        <div style={{ 'marginBottom': '1px' }}>1008 Third Ave, PO Box 449, Sheldon, IA 51201</div>
                                        <div>712.324.4614</div>
                                    </div>
                                </div>
                                <div className='ce-contact-info-container'>
                                    <img className='ce-leaf-icon' src={celeaf} alt="CE Leaf icon" /> 
                                    <div className='ce-contact-info'>
                                        <div style={{ 'marginBottom': '1px' }}>5130 E 57th Street, Sioux Falls, SD 57108</div>
                                        <div>605.610.4611</div>
                                    </div>
                                </div>
                            </div>
                        </div>)
                    : <div className='ce-letterhead-bottom-empty' />}
            </div>
            <div className='ce-portrait-view-w-letterhead'>
                {props.letterHead
                ? <div className='ce-letterhead-top-container'>
                    <img className='ce-logo-circle-img' src={CELogoCircle} alt="CE Logo" />
                    <div className='ce-top-line'></div>
                </div>
                : <div className='ce-letterhead-top-container' />
                }
                <div className='ce-page-content'>
                    <div className='ce-section-graph-right'>
                        <div className='ce-section-title' style={{'marginTop': '12pt'}}>Premium</div>
                        <p className='ce-paragraph' style={{'marginBottom': '0', 'textAlignLast': 'justify'}}>
                            The accompanying graph demonstrates the Company’s growth in recent years.<span className='sentence-end'></span>Premium
                            written, net premium and premium earned {comparePremium} by {premiumWritten}, {netPremiumWritten}, and {premiumEarned}, respectively over the past
                            five 
                        </p>
                        <div>
                            <div className='highchart-right' style={{'marginTop': '5px'}}><HighchartsReact highcharts={Highcharts} containerProps={{ style: { width: '358px', maxHeight: '221px', backgroundColor:'#f0f0f0' } }} options={premiumChart(props.calcs, years, abbreviation, 221)} /></div>
                            <p className='ce-paragraph'>
                                years.<span className='sentence-end'></span>Over the past five years, increases in reinsurance costs combined with growth in the unearned premium reserve have limited the company’s growth in both net written premium and net earned premium.<span className='sentence-end'></span>While gross premium levels have increased, 
                                a greater portion of premium has been ceded to reinsurers and a larger balance has remained unearned at year-end, which 
                                has tempered the overall growth reflected in net premium metrics.<span className='sentence-end'></span>
                            </p>
                            <p className='ce-paragraph' style={{'marginBottom': '24pt'}}>An insurer’s operating stability – i.e., its ability to
                                withstand losses from unfavorable underwriting
                                results, catastrophes, and adverse economic
                                developments – depends on its capital and
                                leverage.
                            </p>
                        </div>
                    </div>
                    <div className='ce-section-graph-right'>
                        <div>
                            <div className='ce-section-title'>Leverage</div>
                            <div>
                                <div className='highchart-right'><HighchartsReact highcharts={Highcharts} containerProps={{ style: { width: '358px', maxHeight: '224px', backgroundColor:'#f0f0f0' } }} options={netPremiumWrittenToSurplus(props.calcs, years, abbreviation, 224)} /></div>
                                <p className='ce-paragraph'>
                                    The first leverage test we want to discuss is the <span style={{'fontStyle': 'italic'}}>net premium-to-surplus ratio.</span> – also referred to as the
                                    capacity ratio.
                                </p>
                                <p className='ce-paragraph'>
                                    It is calculated by comparing annual net premium written to the amount of surplus at year
                                    end.<span className='sentence-end'></span>A lower percentage represents a greater ability
                                    to support premium growth.<span className='sentence-end'></span>The net written
                                    premium-to-surplus ratio for {abbreviation} at December 31, {fye}, is {net_premium_to_surplus_ratio}.<span className='sentence-end'></span>The same ratio for the peer group was {peers_net_premium_to_surplus_ratio}, ranging from a low of {min_peers_net_premium_to_surplus_ratio} to a high of {max_peers_net_premium_to_surplus_ratio}.<span className='sentence-end'></span>A.M. Best considers this ratio to be unfavorable if it
                                    exceeds 200%.<span className='sentence-end'></span>NAIC considers capacity ratios in
                                    excess of 300% to be problematic.<span className='sentence-end'></span>As you can
                                    determine from the graph, both {abbreviation} and the peer group are safely within the favorable range.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                {props.letterHead
                    ? (<div className='ce-letterhead-intro-bottom'>
                            <div className='ce-letterhead-bottom-slogan'>
                                <div className='ce-slogan'>
                                    BEYOND THE NUMBERS
                                </div>
                            </div>
                            <div className='ce-page-footer'>2</div>
                        </div>)
                    : (<div className='ce-letterhead-bottom-pagenum-only'>
                            <div className='ce-page-footer'>2</div>
                        </div>)
                }
            </div>
            <div className='ce-portrait-view-w-letterhead'>
                {props.letterHead
                ? <div className='ce-letterhead-top-container'>
                    <img className='ce-logo-circle-img' src={CELogoCircle} alt="CE Logo" />
                    <div className='ce-top-line'></div>
                </div>
                : <div className='ce-letterhead-top-container' />
                }
                <div className='ce-page-content'>
                    <div className='ce-section-graph-right' style={{'minHeight': '200px'}}>
                        <div className='ce-section-title' style={{'marginTop': '12pt'}}>Leverage (Continued)</div>
                        <p className='ce-paragraph' style={{'lineHeight': '1.25', 'marginBottom': '0', 'textAlignLast': 'justify'}}>
                            The second leverage test is the <span style={{'fontStyle': 'italic'}}>net leverage ratio</span>.<span className='sentence-end'></span>The ratio is the sum of the net premium-to-surplus ratio and the ratio determined by dividing net liabilities by the policyholders’ surplus.
                        </p>
                        <div>
                            <div className='highchart-right' style={{'marginTop': '5px'}}><HighchartsReact highcharts={Highcharts} containerProps={{ style: { width: '352px', maxHeight: '230px', backgroundColor:'#f0f0f0' } }} options={netLeverageRatio(props.calcs, years, abbreviation, 230)} /></div>
                            <p className='ce-paragraph' style={{'lineHeight': '1.25', 'marginBottom': '24pt'}}>
                                This ratio measures the Company’s exposures to both pricing errors and errors in estimating liabilities in relation
                                to policyholders’ surplus.<span className='sentence-end'></span>The net leverage ratio
                                for {abbreviation} at December 31, {fye}, is {net_leverage_ratio}.<span className='sentence-end'></span>The
                                same ratio for the peer group is {peers_net_leverage_ratio}, ranging
                                from a low of {min_peers_net_leverage_ratio} to a high of {max_peers_net_leverage_ratio}.<span className='sentence-end'></span>A.M. Best
                                considers a ratio under 400% for property insurers to be acceptable.<span className='sentence-end'></span>Again, both {abbreviation} and the peer group are well within the favorable range.
                            </p>
                        </div>
                    </div>
                    <div className="ce-section-graph-right">
                        <div className='ce-section-title'>Liquidity</div>
                        <p className='ce-paragraph' style={{'lineHeight': '1.25', 'marginBottom': '0', 'textAlignLast': 'justify', 'wordSpacing': '2.5px'}}>
                            Liquidity is a measure of management’s ability to
                            generate sufficient cash flows to meet the short
                            and long term cash requirements for business
                            operations.<span className='sentence-end'></span>After satisfying cash requirements,
                            excess cash flows are used to build the investment
                            portfolio and thereby increase future investment
                            income.<span className='sentence-end'></span>The <span style={{'fontStyle': 'italic'}}>liquidity ratio</span> is calculated by dividing 
                        </p>
                        <div>
                            <div className='highchart-right' style={{'marginTop': '5px'}}>
                                <HighchartsReact
                                    highcharts={Highcharts}
                                    containerProps={{ style: {
                                        width: '352px',
                                        height: '230px',
                                    } }}
                                    options={liquidityRatio(props.calcs, years, abbreviation, true, 230)}
                                />
                            </div>
                            <p className='ce-paragraph' style={{'lineHeight': '1.25', 'wordSpacing': '2.5px'}}>
                                admitted assets by total liabilities.<span className='sentence-end'></span>It
                                indicates the Company’s ability to pay net
                                liabilities with total assets.<span className='sentence-end'></span>The liquidity ratio for {abbreviation} at December 31, {fye}, is {liquidity_ratio}.<span className='sentence-end'></span>The same
                                ratio for the peer group was {peers_liquidity_ratio}, ranging from a
                                low of {min_peers_liquidity_ratio} to a high of {max_peers_liquidity_ratio}.<span className='sentence-end'></span>For property insurers, a result below 140% is considered abnormal by A.M.
                                Best.<span className='sentence-end'></span>Both {abbreviation} and the peer group are well within normal ranges.
                            </p>
                        </div>
                    </div>
                </div>
                {props.letterHead
                    ? (<div className='ce-letterhead-intro-bottom'>
                            <div className='ce-letterhead-bottom-slogan'>
                                <div className='ce-slogan'>
                                    BEYOND THE NUMBERS
                                </div>
                            </div>
                            <div className='ce-page-footer'>3</div>
                        </div>)
                    : (<div className='ce-letterhead-bottom-pagenum-only'>
                            <div className='ce-page-footer'>3</div>
                        </div>)
                }
            </div>
            <div className='ce-portrait-view-w-letterhead'>
                {props.letterHead
                ? <div className='ce-letterhead-top-container'>
                    <img className='ce-logo-circle-img' src={CELogoCircle} alt="CE Logo" />
                    <div className='ce-top-line'></div>
                </div>
                : <div className='ce-letterhead-top-container' />
                }
                <div className='ce-page-content'>
                    <div className='ce-section-graph-right' style={{'minHeight': '200px'}}>
                        <div className='ce-section-title' style={{'marginTop': '12pt'}}>Profitability</div>
                        <div>
                            <div className='highchart-right'><HighchartsReact highcharts={Highcharts} containerProps={{ style: { width: '350px', maxHeight: '222px', backgroundColor:'#f0f0f0' } }} options={combinedRatio(props.calcs, years, abbreviation, 'combined_ratio_no_one_year', 'Combined Ratio - 1-Yr', 222)} /></div>
                            <div>
                                <p className='ce-paragraph' style={{'marginBottom': '0', 'textAlignLast': 'justify'}}>
                                    The Company’s underwriting results for {fye} reflect a {compareUnderwritingGainLoss} of {underwriting_gain_loss}.
                                </p>
                                <p className='ce-paragraph'>
                                    Premium earned, losses incurred, and operating expenses {compareNetLossAndOpex} from the previous year by {net_premium_writtenYOY}, {net_losses_incurredYOY}, and {operating_expensesYOY},
                                    respectively.<span className='sentence-end'></span>Comparable results for the peer group were {peers_net_premium_writtenYOY}, {peers_net_losses_incurredYOY}, and {peers_operating_expensesYOY}, respectively.
                                </p>
                                <p className='ce-paragraph' style={{'marginBottom': '0', 'textAlignLast': 'justify'}}>
                                    The combined loss and expense ratio,
                                    expressed as a percentage, is the key
                                    measure of underwriting profitability.
                                </p>
                                <p className='ce-paragraph' style={{'wordSpacing': '2px'}}>
                                    When the <span style={{ 'fontStyle': 'italic' }}>combined ratio</span> is under 100%,
                                    underwriting results are profitable; when the combined ratio exceeds 100%, underwriting results are unprofitable.<span className='sentence-end'></span>The combined ratio for {abbreviation} for {fye} was {combined_ratio_no_one_year}.<span className='sentence-end'></span>The average {fye} combined ratio for all companies 
                                    in the peer group was {peers_combined_ratio_no_one_year}, ranging from a low of {min_peers_combined_ratio_no_one_year} to a high of {max_peers_combined_ratio_no_one_year}. 
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className='ce-section-graph-right' style={{'minHeight': '200px'}}>
                        <p className='ce-paragraph' style={{'marginBottom': '0', 'textAlignLast': 'justify', 'wordSpacing': '2px'}}>
                            We feel that underwriting performance cannot be
                            measured fairly from the results of only one year.<span className='sentence-end'></span>A catastrophic event could produce an abnormally
                            high combined ratio in any given year, but provide very little evidence to measure the effectiveness of
                            the company’s underwriting and loss control policies and procedures.<span className='sentence-end'></span>For this reason, we like to track 
                        </p>
                        <div>
                            <div className='highchart-right' style={{'marginTop': '5px'}}><HighchartsReact highcharts={Highcharts} containerProps={{ style: { width: '350px', maxHeight: '222px', backgroundColor:'#f0f0f0' } }} options={combinedRatio(props.calcs, years, abbreviation, 'combined_ratio_no_five_year_avg', 'Combined Ratio - 5-Yr Avg', 222)} /></div>
                            <div>
                                <p className='ce-paragraph' style={{'lineHeight': '1.3', 'wordSpacing': '2px'}}>
                                    combined ratios for a period of five years.<span className='sentence-end'></span>In our opinion, analyzing rolling five-year
                                    averages of the combined ratio helps in smoothing the results and more accurately measures overall
                                    underwriting performance.<span className='sentence-end'></span>The average combined ratio for {abbreviation} over the past five years is {combined_ratio_no_five_year_avg}; for the peer group the five-year average combined
                                    ratio is {peers_combined_ratio_no_five_year_avg}, ranging from a low of {min_peers_combined_ratio_no_five_year_avg} to a high of {max_peers_combined_ratio_no_five_year_avg}. 
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                {props.letterHead
                    ? (<div className='ce-letterhead-intro-bottom'>
                            <div className='ce-letterhead-bottom-slogan'>
                                <div className='ce-slogan'>
                                    BEYOND THE NUMBERS
                                </div>
                            </div>
                            <div className='ce-page-footer'>4</div>
                        </div>)
                    : (<div className='ce-letterhead-bottom-pagenum-only'>
                            <div className='ce-page-footer'>4</div>
                        </div>)
                }
            </div>
            <div className='ce-portrait-view-w-letterhead'>
                {props.letterHead
                    ? <div className='ce-letterhead-top-container'>
                        <img className='ce-logo-circle-img' src={CELogoCircle} alt="CE Logo" />
                        <div className='ce-top-line'></div>
                    </div>
                    : <div className='ce-letterhead-top-container' />
                }
                <div className='ce-page-content'>
                    <div className='ce-section-graph-right' style={{'minHeight': '226px'}}>
                        <div className='ce-section-title' style={{'marginTop': '12pt'}}>Operating Efficiency</div>
                        <p className='ce-paragraph' style={{'wordSpacing': '2px'}}>
                            There are two key ratios we recommend tracking to
                            measure management’s ability to gain operating
                            efficiency.
                        </p>
                        <div>
                            <div className='highchart-right'><HighchartsReact highcharts={Highcharts} containerProps={{ style: { width: '350px', maxHeight: '210px', backgroundColor:'#f0f0f0' } }} options={opexRatio(props.calcs, years, abbreviation, 'Expense Ratio')} /></div>
                            <div>
                                <p className='ce-paragraph' style={{'wordSpacing': '2px'}}>
                                    The first is the <span style={{ 'fontStyle': 'italic' }}>expense ratio</span>.<span className='sentence-end'></span>It is calculated by
                                    comparing annual operating expense to premium
                                    written.<span className='sentence-end'></span>The expense ratio for {abbreviation} for {fye} was {operating_expense_ratio}.<span className='sentence-end'></span>A lower expense ratio allows the Company to
                                    absorb a higher loss ratio and still have the
                                    opportunity to experience favorable underwriting
                                    results.<span className='sentence-end'></span>The expense ratio of the peer group is {peers_operating_expense_ratio},
                                    ranging from a low of {min_peers_operating_expense_ratio} to a high of {max_peers_operating_expense_ratio}. 
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className='ce-section-graph-right' style={{'minHeight': '217px'}}>
                        <div>
                            <div className='highchart-right'><HighchartsReact highcharts={Highcharts} containerProps={{ style: { width: '350px', maxHeight: '214px', backgroundColor:'#f0f0f0' } }} options={breakEvenRatio(props.calcs, years, abbreviation, 'break_even_ratio_no', 'Break-Even Loss Ratio', true, 214)} /></div>
                            <div>
                                <p className='ce-paragraph' style={{'wordSpacing': '2px'}}>
                                    The second is the <span style={{ 'fontStyle': 'italic' }}>break-even ratio</span>.<span className='sentence-end'></span>It is calculated using the following formula:
                                </p>
                                <div style={{ 'display': 'flex', 'flexDirection': 'column', 'alignItems': 'center', 'marginBottom': '10px' }}>
                                    <span>Combined ratio at break-even – i.e.,</span>
                                    <span>100% – minus the expense ratio</span>
                                </div>
                                <p className='ce-paragraph' style={{'wordSpacing': '2px'}}>
                                    The ratio represents the point at which any increase in the loss ratio will produce an underwriting loss.<span className='sentence-end'></span>To look at it another way, the higher the break-even
                                    loss ratio, the more losses the company can absorb before underwriting results become unprofitable.<span className='sentence-end'></span>{abbreviation} had a break-even ratio of {break_even_ratio} for {fye} compared to an actual loss ratio of {net_loss_ratio}.<span className='sentence-end'></span>The break-even ratio for the peer group was {peers_break_even_ratio}, ranging from a low
                                    of {min_peers_break_even_ratio} to a high of {max_peers_break_even_ratio}. 
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                {props.letterHead
                    ? (<div className='ce-letterhead-intro-bottom'>
                            <div className='ce-letterhead-bottom-slogan'>
                                <div className='ce-slogan'>
                                    BEYOND THE NUMBERS
                                </div>
                            </div>
                            <div className='ce-page-footer'>5</div>
                        </div>)
                    : (<div className='ce-letterhead-bottom-pagenum-only'>
                            <div className='ce-page-footer'>5</div>
                        </div>)
                }
            </div>
            <div className='ce-portrait-view-w-letterhead'>
                {props.letterHead
                ? <div className='ce-letterhead-top-container'>
                    <img className='ce-logo-circle-img' src={CELogoCircle} alt="CE Logo" />
                    <div className='ce-top-line'></div>
                </div>
                : <div className='ce-letterhead-top-container' />
                }
                <div className='ce-page-content'>
                    <div className='ce-section-graph-right' style={{'marginTop': '12pt', 'minHeight': '217px'}}>
                        <div>
                            <div className='highchart-right'><HighchartsReact highcharts={Highcharts} containerProps={{ style: { width: '350px', maxHeight: '209px', backgroundColor:'#f0f0f0' } }} options={reinsuranceToGPW(props.calcs, years, abbreviation, 209)} /></div>
                            <div>
                                <div className='ce-section-title'>Reinsurance</div>
                                <p className='ce-paragraph' style={{'wordSpacing': 'normal'}}>
                                    The pace of reinsurance rate increases has moderated compared to peak hard-<span className='no-break-span'>market conditions</span>  in prior years.<span className='sentence-end'></span>The
                                    accompanying graph illustrates the trend in reinsurance cost for {abbreviation}.<span className='sentence-end'></span>In {fye}, the cost
                                    of reinsurance for {abbreviation} was {reinsurance_to_gpw_ratio} of premium written.<span className='sentence-end'></span>The same ratio for the peer group
                                    was {peers_reinsurance_to_gpw_ratio}, ranging from a low of {min_peers_reinsurance_to_gpw_ratio} to a high of {max_peers_reinsurance_to_gpw_ratio}.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className='ce-section-graph-right' style={{'marginTop': '24pt'}}>
                        <div className='highchart-right'><HighchartsReact highcharts={Highcharts} containerProps={{ style: { width: '350px', maxHeight: '209px', backgroundColor:'#f0f0f0' } }} options={surplusToGRIF000s(props.calcs, years, abbreviation, 209)} /></div>
                        <div>
                            <div className='ce-section-title'>Financial Resources</div>
                            <p className='ce-paragraph' lang="en" style={{ 'hyphens': 'auto', 'WebkitHyphens': 'auto', 'MsHyphens': 'auto' }}>
                                <span style={{ 'fontStyle': 'italic' }}>Financial resources</span> provide protection for
                                policyholders and the financial strength to
                                support continued business growth.<span className='sentence-end'></span>At
                                December 31, {fye}, {abbreviation} had total surplus of {surplus}.<span className='sentence-end'></span>Surplus {compare_surplus} by {finalDifSurplus} or {surplusYOY} during {fye}.<span className='sentence-end'></span>The accompanying graph
                                demonstrates how surplus has grown in relation to the growth in gross property risk in force.
                            </p>
                        </div>
                    </div>
                    <div style={{'marginTop': '24pt'}}>
                        <p className='ce-paragraph' style={{'wordSpacing': 'normal'}}>
                            If you have any questions or need clarification on any of the information in this report, please do not
                            hesitate to contact us.<span className='sentence-end'></span>Thank you for giving us the opportunity to serve you.<span className='sentence-end'></span>We are honored to be your
                            accountants and tax advisors.
                        </p>
                        <div className='ce-signature-container' style={{'marginTop': '12pt'}}>
                            <div className='ce-sign-margin'>
                                <img className='ce-logo-jpg' src={CEsignature} alt="CE signature" />
                                <div>Cain Ellsworth & Co., LLP</div>
                                <div>Certified Public Accountants</div>
                            </div>
                        </div>
                        <div>
                            <div>Sheldon, Iowa</div>
                            <div>{formattedDate}</div>
                        </div>
                    </div>
                </div>
                {props.letterHead
                    ? (<div className='ce-letterhead-intro-bottom'>
                            <div className='ce-letterhead-bottom-slogan'>
                                <div className='ce-slogan'>
                                    BEYOND THE NUMBERS
                                </div>
                            </div>
                            <div className='ce-page-footer'>6</div>
                        </div>)
                    : (<div className='ce-letterhead-bottom-pagenum-only'>
                            <div className='ce-page-footer'>6</div>
                        </div>)
                }
            </div>
        </div>
    )
}

export default MinnesotaReport;